<template>
  <div>
    <app-detail-header :show-toggle="true"
      :show-new="false"
      :show-print="entity && !entity.isNew"
      :show-delete="entity && !entity.deleted && !entity.isNew && entity.payments.length == 0 && !gstPaid && !readOnlyView"
      :show-save="entity && !entity.deleted && !readOnlyView"
      @appDetailHeaderButtonClicked="onHeaderButtonClicked" />
    <div class="columns is-gapless">
      <div class="column is-narrow">
        <quickinvoice-side-menu v-if="entity"
          ref="sideMenu"
          :entity-name="`${entity.prefix}${entity.invoiceNo}${entity.suffix}`" />
      </div>
      <div class="column m-3">
        <router-view v-if="entity"
          v-model="entity"
          ref="currentChild"
          :is-tiled="false"
          :key="$route.fullPath"
          :route-name="$route.name"
          :gst-paid="gstPaid"
          :read-only-view="readOnlyView" />
      </div>
    </div>
    <unsaved-modal :active.sync="isUnsavedModalActive"
      @close="closeModal()"
      @skipSave="skipSave()"
      @saveContinue="saveContinue()">
      <p slot="text-title">Unsaved Changes</p>
      <p slot="text-content">There are unsaved changes. Please select action below</p>
    </unsaved-modal>
    <save-conflict-modal :active.sync="isSaveConflictModalActive"
      @close="closeModal()"
      @reload="reloadData()">
      <p slot="text-title">Change conflict</p>
      <p slot="text-content">The data on the server is newer than the local copy. Please reload local data.</p>
    </save-conflict-modal>
    <confirm-modal v-if="entity"
      :active.sync="isConfirmModalActive"
      @ok="deleteEntity(true)"
      @cancel="deleteEntity(false)"
      :ok-text="'Yes'"
      :cancel-text="'No'">
      <p slot="text-title">Delete Quick Invoice</p>
      <p slot="text-content">
        Invoice
        <span class="has-text-primary has-text-weight-bold">{{ `${entity.prefix}${entity.invoiceNo}${entity.suffix}` }}</span> will be deleted. Continue?
        <!-- <span class="tag is-danger">In-Active</span> anyway? -->
      </p>
    </confirm-modal>
  </div>
</template>

<script>
import QuickInvoiceSideMenu from './QuickInvoiceSideMenu'
import QuickInvoiceService from './QuickInvoiceService'
import QuickInvoiceValidation from './QuickInvoiceValidation'
import QuickInvoiceRoutes from './route-types'
import CustomerRoutes from '@/views/customer/route-types'
import InsurerRoutes from '@/views/insurer/route-types'
import ContactRoutes from '@/views/contact/route-types'
import AppDetailHeader from '@/components/AppDetailHeader'
import { UnsavedModal, SaveConflictModal, ConfirmModal } from '@/components/BulmaModal'
import HttpStatus from '@/components/http-status'
import { AppHeaderButtonTypes, EventHubTypes, RemarkTypes, AssetTypes } from '@/enums'
import StoreMixin from './storeMixin'
import _cloneDeep from 'lodash/cloneDeep'
import _debounce from 'lodash.debounce'
import _isEmpty from 'lodash/isEmpty'
import PrintPreviewRoutes from '@/components/printpreview/route-types'
import { Emailer } from '@/classes'
import { KeyValuePairModel } from '@/classes/viewmodels'

export default {
  name: 'QuickInvoiceView',
  components: {
    AppDetailHeader,
    [QuickInvoiceSideMenu.name]: QuickInvoiceSideMenu,
    UnsavedModal,
    SaveConflictModal,
    ConfirmModal
  },
  mixins: [QuickInvoiceValidation, StoreMixin],
  props: {
    isNew: Boolean,
    returnUrl: String,
    type: String
  },
  data() {
    return {
      entity: null,
      isUnsavedModalActive: false,
      isSaveConflictModalActive: false,
      isConfirmModalActive: false,
      isSkipSave: false,
      isSaveContinue: false,
      toRoute: null // stores "to" route of beforeRouteLeave
    }
  },
  computed: {
    validateError() {
      return this.$v.$error || this.$v.detailGroup.$error || this.$v.entity.items.$error
    },
    activeItems() {
      return this.entity.items.filter(i => !i.isDeleted)
    },
    gstPaid() {
      return this.entity.gstPaidDate != null
    }
  },
  watch: {
    entity: {
      handler: _debounce(function(newVal) {
        if (newVal) {
          this.$forceUpdate()
          this.saveSnapshot(_cloneDeep(this.entity))
        }
      }, 500),
      deep: true
    }
  },
  async created() {
    if (this.returnUrl) {
      this.persistQueries()
      this.replaceRoute(this.$route.params.invoiceId)
    }
    this.getEntity()
  },
  mounted() {},
  beforeDestroy() {
    if (_isEmpty(this.returnRoute)) {
      this.setReadOnlyView(false)
    }
  },
  methods: {
    onHeaderButtonClicked(action) {
      switch (action) {
        case AppHeaderButtonTypes.AddNew:
          break
        case AppHeaderButtonTypes.Print:
          this.print1()
          break
        case AppHeaderButtonTypes.Delete:
          this.isConfirmModalActive = true
          break
        case AppHeaderButtonTypes.Save:
          this.save()
          break
        case AppHeaderButtonTypes.Cancel:
          this.cancel()
          break
      }
    },
    async getEntity(reset = false) {
      try {
        this.$showSpinner()
        if (reset) {
          this.clearSessionStorage()
          this.clearSnapshots(this.$route.params.invoiceId)
        }
        if (!this.currentSnapshot) {
          await this.getStoreItem(this.$route.params.invoiceId)
        }
        this.entity = _cloneDeep(this.currentSnapshot)
      } catch (e) {
        this.$notification.openMessageXhrError('', e)
      }
      this.$hideSpinner()
    },
    async save(isDelete) {
      const title = 'Quick Invoice'
      if (!isDelete) {
        // Touch here, not only in the child tabs: after a refresh on /items the Detail
        // tab never mounts, so its payer check would otherwise stay untouched and pass.
        this.$v.detailGroup.$touch()
        this.$v.entity.items.$touch()
        if (this.validateError) {
          this.$notification.openNotificationWithType('danger', title, 'Validation errors. Please fix before saving')
          return false
        } else if (this.activeItems.length === 0 || this.entity.totalExGst === 0) {
          this.$notification.openNotificationWithType('danger', title, 'Invoice total cannot be 0')
          return false
        }
      }
      try {
        this.$showSpinner('Saving...')
        let response
        this.saveSnapshot(_cloneDeep(this.entity))
        if (this.entity.isNew) {
          response = await QuickInvoiceService.postEntity(this.entity)
        } else if (this.snapshotDiff) {
          response = await QuickInvoiceService.putQuickInvoice(_cloneDeep(this.entity), this.snapshotDiff)
        } else {
          this.$notification.openNotificationWithType('warning', title, 'No changes. Not saved')
        }
        this.$hideSpinner()
        if (this.isSaveContinue) {
          this.$notification.openNotificationWithType('success', title, isDelete ? 'Quick Invoice deleted' : 'Save successful')
        } else if (response && response.status === HttpStatus.NO_CONTENT) {
          await this.getEntity(true)
          this.$notification.openNotificationWithType('success', title, isDelete ? 'Quick Invoice deleted' : 'Save successful')
        } else if (response && response.status === HttpStatus.CREATED) {
          await this.getEntity(true)
          this.$notification.openNotificationWithType('success', title, 'New Quick Invoice added')
        }
        this.$eventHub.$emit(EventHubTypes.EntitySaved)
        return true
      } catch (e) {
        this.$hideSpinner()
        if (e.response.request.status === HttpStatus.CONFLICT) {
          this.isSaveConflictModalActive = true
        } else {
          this.$notification.openMessageXhrError('Quick Invoice', e)
        }
        return false
      }
    },
    deleteEntity(confirmDelete) {
      this.isConfirmModalActive = false
      if (confirmDelete) {
        this.entity.deleted = true
        // save snapshots immediately not waiting for debounce
        this.save(true)
      }
    },
    cancel() {
      this.saveSnapshot(_cloneDeep(this.entity))
      if (this.snapshotDiff && !this.isSkipSave && !this.isSaveContinue) {
        console.log(this.snapshotDiff)
        this.isUnsavedModalActive = true
      } else {
        if (this.returnUrl) {
          this.clearSessionStorage()
          this.clearSnapshots(this.entity.invoiceId)
          window.location.href = `${process.env.VUE_APP_ROOT_URI}${this.returnUrl}`
        } else if (this.toRoute) {
          this.$router.push(this.toRoute.fullPath)
        } else if (this.returnRoute && !_isEmpty(this.returnRoute)) {
          this.$router.push(this.returnRoute.fullPath)
        } else {
          this.$router.push({
            name: QuickInvoiceRoutes.QuickInvoiceListView.name
          })
        }
      }
    },
    print() {
      const params = {
        reportname: 'AT_QuickInvoice',
        INVOICEID: this.entity.invoiceId,
        REPORTTITLE: this.$user.info.locale !== 'en-MY' ? 'TAX INVOICE' : 'INVOICE',
        COMPANYNAME: encodeURIComponent(this.$userInfo.companyName),
        COMPANYID: this.$userInfo.companyId,
        timezoneoffset: this.$filters.getSiteTimezoneOffset()
      }
      const url =
        `${process.env.VUE_APP_ROOT_URI}/modules/reports/printpreview.aspx?` +
        Object.keys(params)
          .map(e => `${e}=${params[e]}`)
          .join('&')
      window.open(url, '_blank')
    },
    async saveContinue() {
      this.isUnsavedModalActive = false
      this.isSaveContinue = true
      this.isSaveContinue = await this.save()
      if (this.isSaveContinue) {
        this.cancel()
      }
    },
    async print1() {
      const params = {
        // reportname: 'AT_QuickInvoice',
        INVOICEID: this.entity.invoiceId,
        REPORTTITLE: this.$user.info.locale !== 'en-MY' ? 'TAX INVOICE' : 'INVOICE',
        COMPANYNAME: encodeURIComponent(this.$userInfo.companyName),
        COMPANYID: this.$userInfo.companyId,
        timezoneoffset: this.$filters.getSiteTimezoneOffset()
      }
      // Create KeyValuePairs and add to vuex for later retrieval by PrintPreviewEx component
      const keyValuePairs = KeyValuePairModel.convertToKeyValuePairs(params)
      this.addReportParameters(keyValuePairs)

      const assetEmail = await QuickInvoiceService.getAssetEmail(this.entity.assetId, this.entity.assetType)
      // console.log(assetEmail)
      const emailer = new Emailer()
      if (assetEmail) {
        emailer.toEmails.push(assetEmail)
      }
      emailer.assetId = this.entity.invoiceId
      emailer.assetTypes = [AssetTypes.Customer, AssetTypes.Insurer]
      emailer.remark = `Invoice ${this.entity.prefix}${this.entity.invoiceNo}${this.entity.suffix}`
      emailer.remarkTypeId = RemarkTypes.Remark
      emailer.subject = `Invoice ${this.entity.prefix}${this.entity.invoiceNo}${this.entity.suffix}`
      emailer.reportName = `${this.entity.prefix}${this.entity.invoiceNo}${this.entity.suffix}`
      this.addEmailer(emailer)
      this.$router.push({
        name: PrintPreviewRoutes.PrintPreview.name,
        params: { reportName: 'AT_QuickInvoice' },
        query: { parameterId: this.$guid.newGuid(), emailerId: emailer.id }
      })
    },
    skipSave() {
      this.isUnsavedModalActive = false
      this.isSkipSave = true
      this.cancel()
    },
    redirectToRoute() {
      if (this.toRoute) {
        this.$router.push(this.toRoute.fullPath)
      } else if (!this.entity.isNew) {
        this.cancel()
      }
    },
    closeModal() {
      this.isUnsavedModalActive = false
      this.isSaveConflictModalActive = false
      this.isSaveContinue = false
    },
    async reloadData() {
      this.isSaveConflictModalActive = false
      this.isSaveContinue = false
      await this.getEntity(true)
      this.$eventHub.$emit(EventHubTypes.EntityReload)
    },
    persistQueries() {
      if (this.returnUrl) {
        sessionStorage.setItem(`${this.$userInfo.sessionId}|quickinvoice|returnUrl`, this.returnUrl)
      }
      if (this.type) {
        sessionStorage.setItem(`${this.$userInfo.sessionId}|quickinvoice|type`, this.type)
      }
    },
    removeQueries() {
      sessionStorage.removeItem(`${this.$userInfo.sessionId}|quickinvoice|returnUrl`)
      sessionStorage.removeItem(`${this.$userInfo.sessionId}|quickinvoice|type`)
    },
    clearSessionStorage() {
      this.removeQueries()
    },
    replaceRoute(id) {
      const newMeta = Object.assign(this.$route.meta, {
        returnUrl: this.returnUrl,
        type: this.type
      })
      this.$router.replace({
        name: QuickInvoiceRoutes.QuickInvoiceDetail.name,
        params: { invoiceId: id },
        meta: newMeta
      })
    },
    clearRouteMeta() {
      // Reset returnUrl meta in case user clicked on other V2
      if (this.$route.meta.returnUrl) {
        this.$route.meta.returnUrl = null
      }
    }
  },
  beforeRouteLeave: async function(to, from, next) {
    this.saveSnapshot(_cloneDeep(this.entity))
    const isEditCustomerContact =
      to.name === CustomerRoutes.CustomerDetail.name || to.name === ContactRoutes.ContactDetail.name || to.name === InsurerRoutes.InsurerDetail.name
    if (isEditCustomerContact) {
      next()
    } else if (this.snapshotDiff && !this.isSkipSave && !this.isSaveContinue) {
      console.log(this.snapshotDiff)
      this.$router.replace(from.path)
      this.toRoute = to
      this.isUnsavedModalActive = true
    } else {
      // Clean vuex store for now
      this.clearSnapshots(this.entity.invoiceId)
      this.clearSessionStorage()
      this.clearRouteMeta()
      if (to.name !== PrintPreviewRoutes.PrintPreview.name) {
        this.setReturnRoute({})
      }
      next()
    }
  }
}
</script>
